import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Container,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Banner from '../../components/Banner';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useMessage } from '../../components/MessageProvider';
import { useApi } from '../../hooks/useApi';
import type { AdminUser } from '../../lib/apiClient';

const PAGE_SIZE = 20;

function formatEditedAt(value: string | null): string {
  if (!value) {
    return 'Never';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

async function downloadUsersWorkbook(users: AdminUser[]): Promise<void> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Users');
  worksheet.columns = [
    { header: 'Name', key: 'name', width: 28 },
    { header: 'Email', key: 'email', width: 34 },
    { header: 'Plan', key: 'plan', width: 14 },
    { header: 'Documents', key: 'document_count', width: 14 },
    { header: 'Last document edit', key: 'last_document_edited_at', width: 24 },
    { header: 'Admin', key: 'is_admin', width: 12 },
  ];
  worksheet.getRow(1).font = { bold: true };
  users.forEach((user) => {
    worksheet.addRow({
      name: user.name,
      email: user.email || '',
      plan: user.plan,
      document_count: user.document_count,
      last_document_edited_at: user.last_document_edited_at
        ? new Date(user.last_document_edited_at)
        : '',
      is_admin: user.is_admin ? 'Yes' : 'No',
    });
  });
  worksheet.getColumn('last_document_edited_at').numFmt = 'mmm d, yyyy h:mm AM/PM';

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `users-${new Date().toISOString().slice(0, 10)}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function AdminUsers() {
  const { getAdminUsers, deleteAdminUser } = useApi();
  const { showSuccess } = useMessage();
  const [users, setUsers] = React.useState<AdminUser[]>([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<AdminUser | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [showUnnamed, setShowUnnamed] = React.useState(false);

  const loadUsers = React.useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const result = await getAdminUsers(page, false, showUnnamed, { shouldShowError: () => false });
    if ('error' in result) {
      setLoadError(result.error.message || 'Failed to load users.');
    } else {
      setUsers(result.data.users);
      setTotal(result.data.total);
    }
    setIsLoading(false);
  }, [getAdminUsers, page, showUnnamed]);

  React.useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleExport = async () => {
    setIsExporting(true);
    const result = await getAdminUsers(1, true, showUnnamed);
    if ('data' in result) {
      await downloadUsersWorkbook(result.data.users);
    }
    setIsExporting(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setIsDeleting(true);
    const result = await deleteAdminUser(deleteTarget.user_id);
    if ('data' in result) {
      setDeleteTarget(null);
      showSuccess(`${deleteTarget.name} was deleted.`);
      if (users.length === 1 && page > 1) {
        setPage((current) => current - 1);
      } else {
        await loadUsers();
      }
    }
    setIsDeleting(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: (theme) => (theme.palette.mode === 'light' ? '#f4f6f7' : theme.palette.background.default) }}>
      <Banner />
      <Container maxWidth="lg" sx={{ pt: 10, pb: 8 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h4" component="h1" fontWeight="bold">
              Users
            </Typography>
          </Stack>

          {loadError ? <Alert severity="error">{loadError}</Alert> : null}

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={showUnnamed}
                  onChange={(event) => {
                    setPage(1);
                    setShowUnnamed(event.target.checked);
                  }}
                />
              )}
              label="Show unnamed users"
            />
            <Tooltip title="Export all users to Excel">
              <span>
                <IconButton
                  aria-label="Export all users to Excel"
                  onClick={() => void handleExport()}
                  disabled={isExporting}
                  color="primary"
                >
                  {isExporting ? <CircularProgress size={22} /> : <FileDownloadOutlinedIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          <TableContainer component={Paper} elevation={1}>
            <Table aria-label="Users">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">Documents</TableCell>
                  <TableCell>Last document edit</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : users.map((user) => (
                  <TableRow
                    hover
                    key={user.user_id}
                    sx={{
                      '& .delete-user-button': { opacity: 0 },
                      '&:hover .delete-user-button, &:focus-within .delete-user-button': { opacity: 1 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{user.name}</Typography>
                      {user.email && user.email !== user.name ? (
                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{user.plan}</TableCell>
                    <TableCell align="right">{user.document_count}</TableCell>
                    <TableCell>{formatEditedAt(user.last_document_edited_at)}</TableCell>
                    <TableCell padding="checkbox">
                      <Tooltip title={`Delete ${user.name}`}>
                        <IconButton
                          className="delete-user-button"
                          aria-label={`Delete ${user.name}`}
                          color="error"
                          size="small"
                          onClick={() => setDeleteTarget(user)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {total > PAGE_SIZE ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                page={page}
                count={Math.ceil(total / PAGE_SIZE)}
                onChange={(_event, nextPage) => setPage(nextPage)}
                color="primary"
              />
            </Box>
          ) : null}
        </Stack>
      </Container>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete user?"
        message={`Delete ${deleteTarget?.name || 'this user'} and their sole-owned documents, billing records, login, and other associated data? This cannot be undone.`}
        onConfirm={() => {
          if (!isDeleting) {
            void handleDelete();
          }
        }}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteTarget(null);
          }
        }}
      />
    </Box>
  );
}